/**
 * Styled single-choice dropdown (`plain-select`) for short, fixed option lists.
 *
 * A drop-in replacement for a native `<select>` whose value is owned by the
 * parent via the `value` property + the `select` callback. The native control
 * is fine when closed, but its option popup is OS-drawn - unstylable, and prone
 * to overflowing narrow containers like the sidebar. This renders the menu in
 * our own DOM so it matches the combobox look (flat surface, hairline border,
 * accent-glow highlight) and stays anchored to the trigger.
 *
 * Optional search filters longer lists by label, value or extra keywords.
 * Keyboard: ↑/↓ move, Enter/Space open + commit, Esc closes. Reuses the
 * `.combobox-menu` / `.combobox-item` styles; trigger styling lives alongside
 * them in combobox.css.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { LightElement } from '../lit-base.ts';
import { fuzzyScore } from '../utils/fuzzy.ts';

export interface SelectOption {
  value: string;
  label: string;
  /** Extra words matched by the optional search, such as a unit's category. */
  keywords?: string;
  /** Keep actions such as Custom available even when no choices match. */
  alwaysVisible?: boolean;
}

@customElement('plain-select')
export class PlainSelect extends LightElement {
  @property({ attribute: false }) options: SelectOption[] = [];
  /** Committed option value. */
  @property() value = '';
  @property({ type: Boolean }) disabled = false;
  /** Anchor the menu to the trigger's right edge (for controls near an edge). */
  @property({ type: Boolean }) alignRight = false;
  /** Optional search for longer lists; short selects keep their existing behavior. */
  @property({ type: Boolean }) searchable = false;
  @property() searchPlaceholder = 'Search options…';
  @property() label = '';
  /** Called with the chosen value when the user picks an option. */
  @property({ attribute: false }) select: (value: string) => void = () => {};

  @state() private open = false;
  @state() private highlighted = -1;
  @state() private query = '';
  @state() private menuAbove = false;
  @state() private menuHeight = 280;

  /** Keep long, searchable menus inside the viewport and any clipping panel. */
  private placeMenu(): void {
    const trigger = this.querySelector('.plain-select-trigger');
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    let top = 0;
    let bottom = window.innerHeight;
    for (let parent = this.parentElement; parent; parent = parent.parentElement) {
      if (/(auto|scroll|hidden|clip)/.test(getComputedStyle(parent).overflowY)) {
        const bounds = parent.getBoundingClientRect();
        top = Math.max(top, bounds.top);
        bottom = Math.min(bottom, bounds.bottom);
      }
    }
    const below = bottom - rect.bottom - 4;
    const above = rect.top - top - 4;
    this.menuAbove = below < 280 && above > below;
    this.menuHeight = Math.max(0, Math.min(280, this.menuAbove ? above : below));
  }

  private visibleOptions(): SelectOption[] {
    if (!this.searchable || !this.query.trim()) return this.options;
    const scored = this.options.map((option) => ({ option,
      score: option.alwaysVisible ? -Infinity
        : fuzzyScore(this.query, `${option.label} ${option.value} ${option.keywords ?? ''}`),
    })).filter((item): item is { option: SelectOption; score: number } => item.score !== null);
    scored.sort((a, b) => b.score - a.score);
    return scored.map((item) => item.option);
  }

  private get selectedLabel(): string {
    return this.options.find((o) => o.value === this.value)?.label ?? '';
  }

  private openMenu(): void {
    if (this.disabled) return;
    if (this.searchable) this.placeMenu();
    this.query = '';
    this.open = true;
    this.highlighted = Math.max(0, this.options.findIndex((o) => o.value === this.value));
    if (this.searchable) void this.updateComplete.then(() => {
      this.querySelector<HTMLInputElement>('.plain-select-search input')?.focus({ preventScroll: true });
      this.scrollToHighlighted();
    });
  }

  private closeMenu(): void {
    this.open = false;
    this.highlighted = -1;
  }

  private pick(value: string): void {
    this.closeMenu();
    if (value !== this.value) this.value = value;
    if (this.searchable) this.querySelector<HTMLButtonElement>('.plain-select-trigger')?.focus();
    this.select(value);
  }

  private onFocusOut(): void {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closeMenu();
    }, 150);
  }

  private onTriggerClick(): void {
    if (this.open) this.closeMenu();
    else this.openMenu();
  }

  private onKeydown(e: KeyboardEvent): void {
    if (this.disabled) return;
    if (!this.open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openMenu();
      }
      return;
    }
    const options = this.visibleOptions();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.highlighted = Math.min(options.length - 1, this.highlighted + 1);
      this.scrollToHighlighted();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.highlighted = Math.max(0, this.highlighted - 1);
      this.scrollToHighlighted();
    } else if (e.key === 'Enter' || (e.key === ' ' && !(e.target instanceof HTMLInputElement))) {
      e.preventDefault();
      const o = options[this.highlighted];
      if (o) this.pick(o.value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.closeMenu();
      this.querySelector<HTMLButtonElement>('.plain-select-trigger')?.focus();
    }
  }

  private scrollToHighlighted(): void {
    void this.updateComplete.then(() => {
      const items = this.querySelectorAll<HTMLElement>('.combobox-item');
      const item = items[this.highlighted];
      const menu = this.querySelector<HTMLElement>('.combobox-menu');
      if (!item || !menu) return;
      const header = this.querySelector<HTMLElement>('.plain-select-search')?.offsetHeight ?? 0;
      if (item.offsetTop < menu.scrollTop + header) menu.scrollTop = item.offsetTop - header;
      else if (item.offsetTop + item.offsetHeight > menu.scrollTop + menu.clientHeight) {
        menu.scrollTop = item.offsetTop + item.offsetHeight - menu.clientHeight;
      }
    });
  }

  override render(): TemplateResult {
    const options = this.visibleOptions();
    return html`
      <div class="plain-select combobox ${this.open ? 'open' : ''} ${this.alignRight ? 'align-right' : ''}"
        @focusout=${this.onFocusOut}>
        <button
          type="button"
          class="plain-select-trigger"
          ?disabled=${this.disabled}
          aria-haspopup="listbox"
          aria-expanded=${this.open}
          aria-label=${this.label || this.selectedLabel}
          @click=${this.onTriggerClick}
          @keydown=${this.onKeydown}
        >
          <span class="plain-select-value">${this.selectedLabel}</span>
        </button>
        <div class="combobox-menu" style=${styleMap(this.searchable ? {
          top: this.menuAbove ? 'auto' : undefined,
          bottom: this.menuAbove ? 'calc(100% + 4px)' : undefined,
          maxHeight: `${this.menuHeight}px`,
        } : {})}>
          ${this.searchable ? html`<div class="plain-select-search">
            <input type="text" autocomplete="off" spellcheck="false"
              placeholder=${this.searchPlaceholder} aria-label=${this.searchPlaceholder}
              .value=${this.query}
              @input=${(e: Event) => {
                this.query = (e.target as HTMLInputElement).value;
                this.highlighted = this.visibleOptions().length ? 0 : -1;
                const menu = this.querySelector<HTMLElement>('.combobox-menu');
                if (menu) menu.scrollTop = 0;
              }} @keydown=${this.onKeydown} />
          </div>` : nothing}
          <div role="listbox" aria-label=${this.label || this.selectedLabel}>
          ${options.length === 0 ? html`<div class="combobox-empty">No matches</div>` : nothing}
          ${options.map(
            (o, i) => html`
              <div
                class="combobox-item ${i === this.highlighted ? 'active' : ''} ${o.value === this.value
                  ? 'selected'
                  : ''}"
                role="option"
                aria-selected=${o.value === this.value}
                @mousedown=${(e: Event) => {
                  e.preventDefault();
                  this.pick(o.value);
                }}
                @mouseenter=${() => {
                  this.highlighted = i;
                }}
              >
                ${o.label}
              </div>
            `,
          )}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plain-select': PlainSelect;
  }
}
